import type { DeletedNativeProperty, NativeProperty } from '@/api/core/native-property-read';
import type { ParamPatch } from '@/lib/url-state';

/**
 * 지점 목록의 검색·필터 조건.
 * 서버는 지점명 정렬(sort_direction)과 cursor만 받으므로 검색어와 신청 허용 필터는
 * 지금 불러온 한 페이지 안에서만 걸러진다(지점 전체 검색이 아니다). 조건은 주소(`q`·`application`)에 둬서
 * 새로고침·링크 공유·상세에서 목록으로 돌아오기에 남는다.
 */
export const APPLICATION_FILTER_VALUES = ['enabled', 'disabled'] as const;
export type ApplicationFilterValue = (typeof APPLICATION_FILTER_VALUES)[number];

/** 주소로 들어온 검색어 길이 상한. 상세 복귀 주소 검증(native-property-url)도 같은 값을 쓴다. */
export const MAX_PROPERTY_SEARCH_LENGTH = 100;

export interface NativePropertyListQuery {
  q: string;
  application: ApplicationFilterValue | null;
}

export const isApplicationFilterValue = (value: unknown): value is ApplicationFilterValue =>
  APPLICATION_FILTER_VALUES.some((allowed) => allowed === value);

export function parseNativePropertyListQuery(params: URLSearchParams): NativePropertyListQuery {
  const application = params.get('application');
  return {
    q: (params.get('q') ?? '').trim().slice(0, MAX_PROPERTY_SEARCH_LENGTH),
    application: isApplicationFilterValue(application) ? application : null,
  };
}

export const isNativePropertyListFiltered = (query: NativePropertyListQuery) =>
  query.q !== '' || query.application !== null;

/** 지점명·코드·ID 중 하나라도 검색어를 포함하는가(대소문자 무시). 검색어가 없으면 모두 맞는다. */
const matchesKeyword = (row: { name: string; code: string; id: string }, keyword: string) =>
  !keyword ||
  [row.name, row.code, row.id].some((value) => value.toLocaleLowerCase().includes(keyword));

/** 검색어(지점명·코드·ID, 대소문자 무시)와 신청 허용 필터를 모두 만족하는 지점만 남긴다. */
export function filterNativeProperties(
  rows: readonly NativeProperty[],
  query: NativePropertyListQuery,
): NativeProperty[] {
  const keyword = query.q.toLocaleLowerCase();
  return rows.filter((row) => {
    if (query.application === 'enabled' && !row.application_enabled) return false;
    if (query.application === 'disabled' && row.application_enabled) return false;
    return matchesKeyword(row, keyword);
  });
}

/**
 * 삭제된 지점 보기의 검색 — 운영 지점과 같은 검색어(`q`)를 쓴다.
 * 신청 허용은 삭제된 지점에 없는 값이라 그 필터는 이 보기에서 쓰지 않는다.
 */
export function filterDeletedProperties(
  rows: readonly DeletedNativeProperty[],
  query: Pick<NativePropertyListQuery, 'q'>,
): DeletedNativeProperty[] {
  const keyword = query.q.toLocaleLowerCase();
  return rows.filter((row) => matchesKeyword(row, keyword));
}

/** 목록이 보여 주는 대상 — 운영 지점, 또는 Core에서 삭제된 지점(`view=deleted`). */
export type NativePropertyListView = 'active' | 'deleted';

/** 구간(세그먼트) 값. `all`은 신청 허용 조건이 없는 상태다. */
export type ApplicationSegment = 'all' | ApplicationFilterValue;

export const toApplicationSegment = (
  application: ApplicationFilterValue | null,
): ApplicationSegment => application ?? 'all';

/** 구간 값 → URL 패치. `all`과 알 수 없는 값은 조건을 지운다. */
export const patchFromApplicationSegment = (segment: string): ParamPatch => ({
  application: isApplicationFilterValue(segment) ? segment : null,
});

/**
 * 목록 위 안내 한 줄 — 페이지가 나뉘어 있을 때만 말하고, 그렇지 않으면 null.
 * 서버가 전체 건수를 주지 않으므로 지금 보이는 페이지 기준임을 분명히 한다.
 * (조건을 걸 때마다 이 줄이 생기고 사라지면 표가 밀리므로 한 페이지에 다 담기는 목록에서는 말하지 않는다.)
 */
export function nativePropertyListNote({
  loaded,
  filtered,
  paged,
  pageSize,
}: {
  loaded: number;
  filtered: boolean;
  paged: boolean;
  pageSize: number;
}): string | null {
  if (!paged || loaded === 0) return null;
  return filtered
    ? '검색·필터는 지금 보이는 페이지 안에서만 찾습니다. 다른 페이지는 이동한 뒤 다시 찾아 주세요.'
    : `지금 보이는 페이지의 지점 ${String(loaded)}개입니다. 지점명순으로 한 번에 최대 ${String(pageSize)}개씩 보여 줍니다.`;
}

const DELETED_NOTICE =
  'Core에서 삭제된 지점만 표시합니다. 원본에서 삭제되어 Core에 이관되지 않은 자료는 이 목록에 포함되지 않습니다. 삭제된 지점은 선택하거나 판매 설정을 변경할 수 없습니다.';

/**
 * 삭제된 지점 보기 위 안내 — 이 목록이 무엇을 보여 주고 무엇을 뺀 것인지는 항상 말한다.
 * 검색이 걸린 채 페이지가 나뉘어 있으면 검색이 이 페이지 안에서만 걸린다는 말을 덧붙인다.
 */
export function deletedPropertyListNote({
  filtered,
  paged,
}: {
  filtered: boolean;
  paged: boolean;
}): string {
  return filtered && paged
    ? `${DELETED_NOTICE} 검색은 지금 보이는 페이지 안에서만 찾습니다. 다른 페이지는 이동한 뒤 다시 찾아 주세요.`
    : DELETED_NOTICE;
}

/**
 * 표가 비었을 때의 안내 — 데이터 없음, 조건 결과 없음, 비어 버린 페이지를 구분한다(DS C10).
 * 삭제된 지점 보기에는 신청 허용 필터가 없으므로 "검색어"만 말한다.
 */
export function nativePropertyListEmpty({
  view,
  filtered,
  paged,
  hasCursor,
}: {
  view: NativePropertyListView;
  filtered: boolean;
  /** 지금 페이지가 나뉜 목록의 일부인가(이전·다음 페이지가 있다). */
  paged: boolean;
  /** 첫 페이지가 아닌 cursor 주소인가. */
  hasCursor: boolean;
}): { title: string; description: string } {
  const deleted = view === 'deleted';
  if (filtered) {
    const change = deleted ? '검색어를 바꾸거나 초기화' : '검색어나 필터를 바꾸거나 초기화';
    return {
      title: deleted ? '조건에 맞는 삭제된 지점이 없습니다' : '조건에 맞는 지점이 없습니다',
      description: paged
        ? `이 페이지에는 맞는 지점이 없습니다. ${change}하고, 다른 페이지도 확인해 보세요.`
        : `${change}를 눌러 보세요.`,
    };
  }
  if (!hasCursor) {
    return deleted
      ? {
          title: '삭제된 지점이 없습니다',
          description: 'Core에서 삭제된 지점이 생기면 여기에 표시됩니다.',
        }
      : { title: '아직 등록된 지점이 없습니다', description: '지점을 등록하면 여기에 표시됩니다.' };
  }
  return {
    title: deleted
      ? '이 페이지에는 표시할 삭제된 지점이 없습니다'
      : '이 페이지에는 표시할 지점이 없습니다',
    description: '처음으로 돌아가 목록을 다시 확인해 보세요.',
  };
}
